import { cloneDeep } from 'lodash-es';
import { take, drop } from 'lodash/fp'
import noop from 'lodash-es/noop';
import { addFeatureToCollections, createCollection, deleteCollection, getCollectionById, getCollectionsList, getImportedCollections, removeFeaturesFromCollection, updateCollection } from '../services/udapi/collections';
import { LARGE_DATA_SET_COUNT } from '../lib/constants'
import { savePinnedCollection } from '../lib/userPreferences'

export default {
  namespace: 'collections',

  state: {
    collectionsById: {},
    collectionsList: [],
    collectionsLoading: true,
    hasLargeCollectionDataSet: false,
    isCollectionDirty: false,
    isEditingCollection: false,
    moreCollectionsLoading: true,
    newCollectionName: '',
    pagination: {
      limit: 1000,
      offset: 0,
      total: 0,
    },
    preferences: {
      pinned: null
    },
    previousCollectionState: null,
    savingCollection: false,
    selectedCollection: null,
    selectedCollectionLoading: false,
  },

  effects: {
    getUserCollectionPoll: [
      function* ({ callback, payload }, { call, put, select }) {
        const { pagination, sort } = yield select((model) => model.collections);
        let collectionsList = []
        let collectionsObj = {};

        let queryParams = {
          ascending: sort == 'title' ? true : false,
          limit: pagination.limit,
          offset: pagination.offset,
          sort: sort,
        };

        let response = yield call(getCollectionsList, { queryParams });
        if (response.success) {
          collectionsList = response.data;

          let collection = null
          for (let cli = 0; cli < collectionsList.length; cli++) {
            collection = collectionsList[cli]
            collectionsObj[collection.uuid] = collection
          }

          yield put({ type: 'appendCollections', payload: collectionsList });
          yield put({ type: 'setCollectionDict', payload: collectionsObj });
          yield put({ type: 'setCollectionTotalCount', payload: response.data.length + pagination.total });

          if (response.data.length == pagination.limit) {
            yield put({
              type: 'setOffset',
              payload: response.data.length + pagination.offset,
            });
          } else {
            yield put({ type: 'setCollectionsLoading', payload: false });
            yield put({ type: 'setMoreCollectionsLoading', payload: false });
            callback();
          }
        } else {
          yield put({ type: 'setCollectionsLoading', payload: false });
          yield put({ type: 'setMoreCollectionsLoading', payload: false });
          callback();
        }
      },
      { type: 'poll', delay: 500 },
    ],
    *getImportedCollections({ callback = noop, payload }, { call, put, select }) {
      const { importedCollections } = yield select((model) => model.dataManager);
      let collectionIds = importedCollections.map(f => f.uuid)
      let collectionIdsChunk = []
      let collectionsFailed = []
      let collectionsObj = {}
      let collectionsSuccessful = []
      let response = null

      while (collectionIds.length) {
        collectionIdsChunk = take(500, collectionIds)
        collectionIds = drop(500, collectionIds)

        response = yield call(getImportedCollections, collectionIdsChunk);
        if (response.success) {
          if (response.data.successful) {
            collectionsSuccessful = collectionsSuccessful.concat(response.data.successful)
          } else if (response.data.failed) {
            collectionsFailed = collectionsFailed.concat(response.data.failed)
          }
        } else {
          yield put({ type: 'setCollectionsLoading', payload: false });
          yield put({ type: 'setMoreCollectionsLoading', payload: false });
          return callback(null);
        }
      }


      if (collectionsSuccessful.length) {
        if (!collectionsFailed.length) {
          collectionsSuccessful.forEach((collection) => {
            collectionsObj[collection.uuid] = collection;
          });
          yield put({ type: 'appendCollections', payload: collectionsSuccessful });
          yield put({ type: 'setCollectionDict', payload: collectionsObj });
          yield put({ type: 'setCollectionTotalCount', payload: collectionsSuccessful.length });
          callback(collectionsSuccessful[0])
        } else {
          yield put({ type: 'setCollectionsLoading', payload: false });
          yield put({ type: 'setMoreCollectionsLoading', payload: false });
          callback(null);
        }
      } else {
        yield put({ type: 'setCollectionsLoading', payload: false });
        yield put({ type: 'setMoreCollectionsLoading', payload: false });
        callback(null);
      }
    },
    *getCollectionsListEffect({ callback = noop }, { call, put, select }) {
      const { pagination, sort } = yield select((model) => model.collections);
      let collectionsList = [];
      let collectionsObj = {};
      /*
        #TODO: It's possible to rewrite this effect as a poll instead,
        similar to how features are retrieved, however, users currently
        only have around ~10 collections (hunts), so a hard limit will
        be passed into the UDAPI call instead. This may change in the
        future when collections are more widely used.
        -- WD - December 21, 2021
      */
        let queryParams = {
          ascending: sort == 'title' ? true : false,
          limit: pagination.limit,
          offset: pagination.offset,
          sort: sort,
        };

      let response = yield call(getCollectionsList, { queryParams });
      if (response.success) {        
        collectionsList = response.data;

        collectionsList.forEach((collection) => {
          collectionsObj[collection.uuid] = collection;
        });

        callback(collectionsList);
      }

      yield put({
        type: 'setHasLargeDataSet',
        payload: collectionsList.length > LARGE_DATA_SET_COUNT.Collections
      });

      yield put({ type: 'setCollectionsList', payload: collectionsList });
      yield put({ type: 'setCollectionDict', payload: collectionsObj });
      yield put({ type: 'setCollectionsLoading', payload: false });
      callback()
    },
    *createCollectionEffect({ payload, callback = noop }, { call, put, select }) {
      const collectionsList = yield select((model) => model.collections.collectionsList);
      let response = yield call(createCollection, { title: payload });

      if (response.success) {
        yield put({
          type: 'setHasLargeDataSet',
          payload: collectionsList.length + 1 > LARGE_DATA_SET_COUNT.Collections
        });
        
        callback(response.data.uuid);
      } else {
        callback(null);
        console.error('ERROR: Unable to create hunt', response);
      }
    },
    *getCollectionEffect({ callback = noop, payload: collectionId }, { call, put }) {
      const collectionRes = yield call(getCollectionById, collectionId);

      if (collectionRes.success) {
        callback(collectionRes.data);
      } else {
        callback(null, collectionRes.data.response);
        console.error('ERROR: Unable to retrieve hunt', collectionRes);
      }
    },
    *getSelectedCollection({ callback = noop}, { select }) {
			const { selectedCollection } = yield select((model) => model.collections)
			callback(selectedCollection)
		},
    *updateCollectionEffect({ callback = noop, payload }, { call, put, select }) {
      const { collectionId, updates } = payload;

      const collectionsList = yield select((model) => model.collections.collectionsList);
      const collectionIdx = collectionsList.findIndex((col) => col.uuid === collectionId);

      if (collectionIdx < 0) {
        callback();
      }

      const collection = collectionsList[collectionIdx];
      const updatedCollection = { ...collection, ...updates };

      if (!collection || !collection.uuid) {
        callback();
      }

      yield put({ type: 'setCollectionSaving', payload: true });

      let response = yield call(updateCollection, updatedCollection);
      if (response.success) {
        yield put({ type: 'updateCollectionsList', payload: updatedCollection });
        yield put({ type: 'selectCollection', payload: updatedCollection });
        yield put({ type: 'setCollectionSaving', payload: false });
        callback(updatedCollection);
      } else {
        console.error('ERROR: Unable to update hunt', response);
        callback();
      }
    },
    *deleteCollectionEffect({ callback = noop, payload: uuid }, { call, put, select }) {
      const { collectionsList, preferences } = yield select((model) => model.collections);
      const collectionIdx = collectionsList.findIndex((col) => col.uuid == uuid)
      let collection = null;

      if (collectionIdx != -1) {
        collection = collectionsList[collectionIdx];
      }

      if (!collection || !collection.uuid) {
        callback();
      }

      /* 
        At this moment, notes are automatically deleted.
        This may change in the near future and that is
        when we can implement a prompt for the user to
        decide if the want to delete the notes.
        - June 10th, 2022
      */
      const params = {
        queryParams: {
          deleteNotes: true
        }
      }
      let response = yield call(deleteCollection, collection, params);
      if (response.success) {
        yield put({
          type: 'deleteHuntFromUserData',
          payload: collection.uuid
        });

        if (preferences.pinned === collection.uuid) {
          yield put({ type: 'setPinnedCollectionId', payload: null })
        }
        callback(collection);
      } else {
        console.error('ERROR: Unable to delete hunt', response);
        callback();
      }
    },
    *addFeatureToCollectionsEffect({ callback = noop, payload }, { call, select }) {
      // This is currently not being used
      // We use the same effect from features instead - WD
      const { featureId, collectionIds } = payload;

      const response = yield call(addFeatureToCollections, [featureId], collectionIds);
      if (response.success) {
        callback(response);
      } else {
        console.error('ERROR: Unable to add feature to hunts', response);
        callback(null);
      }
    },
    *removeFeaturesFromCollectionEffect({ payload, callback = noop }, { call, select }) {
      const collectionsList = yield select((model) => model.collections.collectionsList);

      const collection = collectionsList.find(cl => cl.uuid === payload.collectionId);
      if (!collection) {
        callback(null);
      }

      const deleteRes = yield call(removeFeaturesFromCollection, payload.featureIds, payload.collectionId);

      if (deleteRes.success) {
        callback(deleteRes);
      } else {
        console.error('ERROR: Unable to update hunts', deleteRes);
        callback(null);
      }
    },
    *toggleAllCollectionCheckboxes(action, { put, select }) {
      const featuresList = yield select((model) => model.collections.selectedCollection.features);
      const selectedFeatures = yield select((model) => model.features.selectedFeatures);
      const featureIds = featuresList.map(feature => feature.uuid);

      if (featuresList.length !== selectedFeatures.length) {
        yield put({
          type: 'features/addToSelectedFeatures',
          payload: {
            type: 'multiple',
            featureIds: featureIds
          }
        });
      } else {
        yield put({
          type: 'features/resetAllSelectedFeatures'
        });
      }
    },
    *revertCollectionToPreviousCollectionState({ callback = noop }, { put, select }) {
      const { previousCollectionState } = yield select((model) => model.collections);

      if (previousCollectionState) {
        yield put({ type: 'updateCollectionsList', payload: previousCollectionState });

        callback(previousCollectionState);
      }
    },
    *setPinnedCollection({ payload }, { put }) {
      yield put({ type: 'setPinnedCollectionId', payload })
    },
    *setLoadingCollections({ callback = noop, payload }, { call, put, select }) {
			if (payload.type === 'collectionsList') {
				yield put({
					type: 'setCollectionsLoading',
					payload: payload.status,
				})
			} else if (payload.type === 'selectedCollection') {
				yield put({
					type: 'setSelectedCollectionLoading',
					payload: payload.status,
				})
			}
			callback(null)
		}
  },
  reducers: {
    appendCollections(state, { payload: collections }) {
      const collectionsList = [...state.collectionsList, ...collections]
      const hasLargeCollectionDataSet = collectionsList.length >= LARGE_DATA_SET_COUNT.Collections 
      return {
        ...state,
        collectionsList,
        hasLargeCollectionDataSet
      };
    },
    prependCollections(state, { payload: collections }) {
      const collectionsList = [...collections, ...state.collectionsList]
      const hasLargeCollectionDataSet = collectionsList.length >= LARGE_DATA_SET_COUNT.Collections 
      return {
        ...state,
        collectionsList,
        hasLargeCollectionDataSet
      };
    },
    addNewHuntToUserData(state, { payload: collection }) {
      const collectionsList = [collection, ...state.collectionsList]
      const hasLargeCollectionDataSet = collectionsList.length >= LARGE_DATA_SET_COUNT.Collections 
      return {
        ...state,
        collectionsList,
        hasLargeCollectionDataSet
      };
    },
    deleteHuntFromUserData(state, { payload: collectionId }) {
      const collectionsList = [...state.collectionsList]
      const collectionIndex = collectionsList.findIndex(cl => cl.uuid === collectionId)
      if (collectionIndex > -1) {
        collectionsList.splice(collectionIndex, 1)
      }
      const hasLargeCollectionDataSet = collectionsList.length >= LARGE_DATA_SET_COUNT.Collections 
      return {
        ...state,
        collectionsList,
        hasLargeCollectionDataSet
      };
    },
    deleteCollectionFromUserHunts(state, { payload: collectionId }) {
      const collectionsList = [...state.collectionsList]
      const collectionIndex = collectionsList.findIndex(cl => cl.uuid === collectionId)
      if (collectionIndex > -1) {
        collectionsList.splice(collectionIndex, 1)
      }
      return {
        ...state,
        collectionsList
      };
    },
    addFeatureToCollectionsList(state, { payload }) {
      const newCollectionsList = state.collectionsList
      for (let cli = 0; cli < newCollectionsList.length; cli++) {
        if (payload.collectionIds.includes(newCollectionsList[cli].uuid)) {
          if (!newCollectionsList[cli].featureIds.includes(payload.featureId)) {
            newCollectionsList[cli].featureIds = [...newCollectionsList[cli].featureIds, payload.featureId]
            newCollectionsList[cli].count = newCollectionsList[cli].featureIds.length
          }
        }
      }
      return {
        ...state,
        collectionsList: newCollectionsList
      };
    },
    removeFeatureFromCollectionsList(state, { payload }) {
      // This function removes a single feature from a collection and returns
      // the updated collectionList
      const { collectionsList, selectedCollection } = state

      const collectionIndex = collectionsList.findIndex(cl => cl.uuid === payload.collectionId)
      if (collectionIndex > -1) {
        const collection = collectionsList[collectionIndex]
        const newFeatureIds = []
        for (let fidi = 0; fidi < collection.featureIds.length; fidi++) {
          if (collection.featureIds[fidi] !== payload.featureId) {
            newFeatureIds.push(collection.featureIds[fidi])
          }
        }
        collection.count = newFeatureIds.length;
        collection.featureIds = newFeatureIds;

        if (selectedCollection && selectedCollection.uuid === collection.uuid) {
          selectedCollection.count = newFeatureIds.length;
          selectedCollection.featureIds = newFeatureIds;
        }
      }
      return {
        ...state,
        collectionsList,
        selectedCollection,
      };
    },
    removeFeaturesFromCollectionsList(state, { payload }) {
       // This function removes multiple features from a collection and returns
      // the updated collectionList
      const { collectionsList, selectedCollection } = state

      const collectionIndex = collectionsList.findIndex(ncl => ncl.uuid === payload.collectionId)
      if (collectionIndex > -1) {
        const collection = collectionsList[collectionIndex]
        const newFeatureIds = []
        for (let fidi = 0; fidi < collection.featureIds.length; fidi++) {
          if (!payload.featureIds.includes(collection.featureIds[fidi])) {
            newFeatureIds.push(collection.featureIds[fidi])
          }
        }
        collection.count = newFeatureIds.length;
        collection.featureIds = newFeatureIds;

        if (selectedCollection && selectedCollection.uuid === collection.uuid) {
          selectedCollection.featureIds = newFeatureIds;
          selectedCollection.count = newFeatureIds.length;
        }
      }

      return {
        ...state,
        collectionsList,
        selectedCollection,
      };
    },
    setCollectionsLoading(state, { payload: loading }) {
      return {
        ...state,
        collectionsLoading: loading
      };
    },
    setOffset(state, { payload: offset }) {
      return {
        ...state,
        pagination: {
          ...state.pagination,
          offset,
        },
      };
    },
    setCollectionTotalCount(state, { payload: count }) {
      return {
        ...state,
        pagination: {
          ...state.pagination,
          total: count,
        },
      };
    },
    setMoreCollectionsLoading(state, { payload: loading }) {
      return {
        ...state,
        moreCollectionsLoading: loading,
      };
    },
    setSelectedCollection(state, { payload: collection }) {
      return {
        ...state,
        selectedCollection: collection,
      };
    },
    setSelectedCollectionLoading(state, { payload: loading }) {
      return {
        ...state,
        selectedCollectionLoading: loading
      };
    },
    setCollectionDict(state, { payload: dict }) {
      return {
        ...state,
        collectionsById: { ...state.collectionsById, ...dict }
      };
    },
    selectCollection(state, { payload: collection }) {
      return {
        ...state,
        selectedCollection: collection
      };
    },
    selectCollectionById(state, { payload: collectionId }) {
      const collectionIndex = state.collectionsList.findIndex(cl => cl.uuid === collectionId)
      let selectedCollection = state.selectedCollection
      if (collectionIndex > -1) {
        selectedCollection = state.collectionsList[collectionIndex]
      }
      return {
        ...state,
        selectedCollection
      };
    },
    setCollectionsList(state, { payload: collectionsList }) {
      return {
        ...state,
        collectionsList
      };
    },
    updateCollectionsList(state, { payload: collection }) {
      let newCollectionsList = state.collectionsList;
      var index = newCollectionsList.findIndex(
        (collectionItem) => collectionItem.uuid === collection.uuid
      );

      if (index < 0) {
        return state;
      }

      newCollectionsList[index] = collection;

      return {
        ...state,
        collectionsList: [...newCollectionsList]
      };
    },
    // setCollectionDirty(state, { payload }) {
    //   return {
    //     ...state,
    //     isCollectionDirty: payload
    //   };
    // },
    setPreviousCollectionState(state, { payload: item }) {
      return {
        ...state,
        previousCollectionState: cloneDeep(item)
      };
    },
    setEditingCollection(state, { payload: status }) {
      return {
        ...state,
        isEditingCollection: status
      };
    },
    setHasLargeDataSet(state, { payload: status }) {
      return {
        ...state,
        hasLargeCollectionDataSet: status
      };
    },
    toggleEditingCollection(state, _) {
      const { isEditingCollection } = state;

      return {
        ...state,
        isEditingCollection: !isEditingCollection
      };
    },
    setNewCollectionTitle(state, { payload: text }) {
      return {
        ...state,
        newCollectionName: text
      };
    },
    setCollectionSaving(state, { payload: status }) {
      return {
        ...state,
        savingCollection: status
      };
    },
    setPinnedCollectionId(state, { payload }) {
      state.preferences.pinned = payload
      const data = {
        jobs: [{
          action: 'change',
          path: 'global/pinned/collection',
          values: [payload]
        }]
      }
      savePinnedCollection(data)
      return {
        ...state,
      };
    },
    setAllPreferences(state, { payload }) {
			return {
				...state,
				preferences: {
					...payload
				}
			}
    },
  }
};